import React, { useState } from 'react';
import { SousTraitantInfo, SituationSousTraitant } from '../types';
import { PlusCircleIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';

interface SousTraitantSectionProps {
  sousTraitant: SousTraitantInfo;
  onAjouterSituation?: (situation: Omit<SituationSousTraitant, 'id'>) => void;
}

const fmt = (n: number) => n.toLocaleString('fr-FR');

const SousTraitantSection: React.FC<SousTraitantSectionProps> = ({
  sousTraitant,
  onAjouterSituation,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [montantDemande, setMontantDemande] = useState('');
  const [montantAccepte, setMontantAccepte] = useState('');
  const [commentaire, setCommentaire] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const demande = parseFloat(montantDemande.replace(/\s/g, '').replace(',', '.'));
    const accepte = parseFloat(montantAccepte.replace(/\s/g, '').replace(',', '.'));
    if (isNaN(demande) || isNaN(accepte)) return;
    onAjouterSituation?.({
      numero: sousTraitant.situationsSousTraitant.length + 1,
      date: new Date().toISOString().split('T')[0],
      montantDemande: demande,
      montantAccepte: accepte,
      ecart: demande - accepte,
      commentaire: commentaire || undefined,
    });
    setMontantDemande('');
    setMontantAccepte('');
    setCommentaire('');
    setShowForm(false);
  };

  const renderStars = (note?: 1 | 2 | 3 | 4 | 5) => {
    if (!note) return <span className="text-xs text-gray-400">Non évalué</span>;
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          i <= note
            ? <StarSolidIcon key={i} className="w-4 h-4 text-amber-400" />
            : <StarIcon key={i} className="w-4 h-4 text-gray-300" />
        ))}
      </div>
    );
  };

  return (
    <div className="mt-6 pt-4 border-t border-gray-200">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">Sous-traitant</h4>

      {/* Infos contrat */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div>
          <div className="text-xs text-gray-500">Réf. contrat</div>
          <div className="text-sm font-medium text-gray-900">{sousTraitant.referenceContrat}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Date contrat</div>
          <div className="text-sm font-medium text-gray-900">
            {new Date(sousTraitant.dateContrat).toLocaleDateString('fr-FR')}
          </div>
        </div>
        {sousTraitant.ordreDeService && (
          <div>
            <div className="text-xs text-gray-500">Ordre de service</div>
            <div className="text-sm font-medium text-gray-900">{sousTraitant.ordreDeService}</div>
          </div>
        )}
        {sousTraitant.dateOS && (
          <div>
            <div className="text-xs text-gray-500">Date OS</div>
            <div className="text-sm font-medium text-gray-900">
              {new Date(sousTraitant.dateOS).toLocaleDateString('fr-FR')}
            </div>
          </div>
        )}
      </div>

      {/* Pénalités + Évaluation */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div>
          <div className="text-xs text-gray-500">Pénalités/jour</div>
          <div className="text-sm font-medium text-gray-900">{fmt(sousTraitant.penalitesRetardJour)} €</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Pénalités cumulées</div>
          <div className={`text-sm font-medium ${sousTraitant.penalitesCumulees > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {fmt(sousTraitant.penalitesCumulees)} €
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-0.5">Évaluation</div>
          {renderStars(sousTraitant.noteEvaluation)}
        </div>
        {sousTraitant.commentaireEvaluation && (
          <div>
            <div className="text-xs text-gray-500">Commentaire</div>
            <div className="text-xs text-gray-600 italic">{sousTraitant.commentaireEvaluation}</div>
          </div>
        )}
      </div>

      {/* Tableau situations ST */}
      <div className="flex items-center justify-between mb-2">
        <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Situations sous-traitant</h5>
        {onAjouterSituation && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors"
          >
            <PlusCircleIcon className="w-4 h-4 mr-1" />
            Ajouter situation
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-3 p-3 bg-violet-50 rounded-lg border border-violet-100 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Montant demandé (€)</label>
              <input
                type="text"
                value={montantDemande}
                onChange={(e) => setMontantDemande(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Montant accepté (€)</label>
              <input
                type="text"
                value={montantAccepte}
                onChange={(e) => setMontantAccepte(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Commentaire</label>
              <input
                type="text"
                value={commentaire}
                onChange={(e) => setCommentaire(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)}
              className="text-xs text-gray-500 hover:text-gray-700">Annuler</button>
            <button type="submit"
              className="bg-violet-600 text-white rounded px-3 py-1 text-xs font-medium hover:bg-violet-700 transition-colors">
              Valider
            </button>
          </div>
        </form>
      )}

      {sousTraitant.situationsSousTraitant.length > 0 ? (
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
              <th className="py-2 px-2">N°</th>
              <th className="py-2 px-2">Date</th>
              <th className="py-2 px-2 text-right">Demandé</th>
              <th className="py-2 px-2 text-right">Accepté</th>
              <th className="py-2 px-2 text-right">Écart</th>
              <th className="py-2 px-2">Commentaire</th>
            </tr>
          </thead>
          <tbody>
            {sousTraitant.situationsSousTraitant.map(sit => (
              <tr key={sit.id} className="border-b border-gray-100 last:border-b-0">
                <td className="py-1.5 px-2 text-sm font-medium text-gray-900">{sit.numero}</td>
                <td className="py-1.5 px-2 text-sm text-gray-700">
                  {new Date(sit.date).toLocaleDateString('fr-FR')}
                </td>
                <td className="py-1.5 px-2 text-sm text-right text-gray-900">{fmt(sit.montantDemande)} €</td>
                <td className="py-1.5 px-2 text-sm text-right font-semibold text-gray-900">{fmt(sit.montantAccepte)} €</td>
                <td className={`py-1.5 px-2 text-sm text-right font-medium ${sit.ecart > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                  {sit.ecart > 0 ? '-' : ''}{fmt(Math.abs(sit.ecart))} €
                </td>
                <td className="py-1.5 px-2 text-sm text-gray-500">{sit.commentaire || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-sm text-gray-400 italic">Aucune situation sous-traitant</p>
      )}
    </div>
  );
};

export default SousTraitantSection;
